import type { PropsWithChildren, ReactNode } from 'react';

import { Layout, Main } from '../Layout/entry.js';
import { cn } from '../utils/entry.js';
import ModalContent from './ModalContent.js';
import ModalFooter from './ModalFooter.js';
import ModalHeader from './ModalHeader.js';

/**
 * @typedef {import('./ModalSidekick.js').default} ModalSidekick
 */

type ModalProps = PropsWithChildren<{
  /** Sticky header components */
  header?: ReactNode;

  /** Sticky footer components */
  footer?: ReactNode;
  className?: string;
}>;

/**
 * Modal like component that should be used in cases where user interaction is required and be rendered as a full page component, eg. statistic pages in a small device / mobile view.
 *
 * The component should be used in a "full page" context, and should not be used in a "popup" context.
 * This component should be used along with {@link ModalSidekick} to disable rendering of the rest of the page.
 *
 * @param root0 - Component props
 * @param root0.children the children to feed the modal, ie the content.
 * @param root0.header the header of this modal
 * @param root0.footer the footer of this modal
 * @param root0.className additional classnames to add to the modal
 * @returns Modal component
 */
export default function Modal({ children, header, footer, className }: ModalProps): ReactNode {
  return (
    <Layout>
      {header && <ModalHeader className="">{header}</ModalHeader>}

      <ModalContent className={cn(`overflow-y-scroll pb-[80px]`, className)}>
        <Main className="h-full">{children}</Main>
      </ModalContent>

      {footer && <ModalFooter className="fixed bottom-0 left-0 w-full bg-white p-4 shadow-2xl">{footer}</ModalFooter>}
    </Layout>
  );
}
